import React, { createContext, useCallback, useContext, useState } from 'react';

type FileAttachmentContextValue = {
  pendingFile: File | null;
  attachFile: (file: File) => void;
  clearPendingFile: () => void;
  consumePendingFile: () => File | null;
};

const FileAttachmentContext = createContext<FileAttachmentContextValue | null>(
  null
);

export function FileAttachmentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const attachFile = useCallback((file: File) => setPendingFile(file), []);
  const clearPendingFile = useCallback(() => setPendingFile(null), []);
  const consumePendingFile = useCallback(() => {
    const f = pendingFile;
    setPendingFile(null);
    return f;
  }, [pendingFile]);

  return (
    <FileAttachmentContext.Provider
      value={{
        pendingFile,
        attachFile,
        clearPendingFile,
        consumePendingFile,
      }}
    >
      {children}
    </FileAttachmentContext.Provider>
  );
}

export function useFileAttachment(): FileAttachmentContextValue {
  const ctx = useContext(FileAttachmentContext);
  if (!ctx) {
    throw new Error(
      'useFileAttachment must be used inside a FileAttachmentProvider'
    );
  }
  return ctx;
}
